import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAppData } from '../../core/contexts/AppDataContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import {
  mapPrincipalOperationFromApi,
  mapPrincipalOperationToApi,
} from './treasuryApiMappers';
import {
  operationExportHeaders,
  mapOperationsForExport,
  exportTreasuryCSV,
  exportTreasuryExcel,
  exportTreasuryPDF,
  operationImportHeaders,
  triggerTreasuryImport,
  parseTreasuryImport,
} from './treasuryExportHelpers';
import { 
  Plus,
  Search,
  Calendar,
  ChevronDown,
  ChevronUp,
  Filter,
  Download,
  Upload,
  FileText,
  FileSpreadsheet,
  FileOutput,
  List,
  Grid,
  RefreshCcw,
  CheckSquare,
  DollarSign,
  CreditCard,
  Wallet,
  TrendingUp,
  Layers,
  LayoutGrid,
  Shield,
  X,
  CheckCircle,
  Clock,
  XCircle,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import '../../shared/components/custom/CustomComponents.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  QAWAEM_MODAL_OVERLAY_CLASS,
  getQawaemModalPanelClass,
  getQawaemModalPanelStyle,
  QawaemErpViewModal,
} from '../../products/saudi/components/QawaemErpCrudModal';
import { QawaemErpModalFormTabBar } from '../../products/saudi/components/QawaemErpModalFormTabs';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { buildCaisseColumnDefs, useCaisseColumnVisibility, caisseTableColSpan, buildCaisseModalFormTabs, nextCaisseEntityRef, sliceCaisseListItems, useCaisseEmployeeOptions, buildCaisseEmployeeSelectOptions, findCaisseEmployeeMeta, useCaisseAccounts, resolveCaisseAccountBalance, CAISSE_FORM_GRID } from './caisseListPageUtils';
import { SAUDI_DEFAULT_REGISTERS } from './saudiTreasuryDefaults';
import { useTreasuryConfig, useTreasuryListViewMode } from './useTreasuryConfig';
import { useSettings } from '../../core/contexts/SettingsContext';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import { QawaemTableStickyActionsTd } from '../../products/saudi/components/QawaemTableStickyActions';

const CaissePrincipalePage = () => {
  const { t, i18n } = useTranslation();
  const treasuryConfig = useTreasuryConfig();
  const [viewMode, setViewMode] = useTreasuryListViewMode(treasuryConfig);
  const { settings } = useSettings();
  const p = (key, opts) => t(`modules.caisse.pages.caissePrincipalePage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const cfgTheme = useModuleConfigTheme();
  const isLight = cfgTheme.light;
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: 'العملية' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'العمليات' });
  const {
    transferPrincipaleToMensuelle,
  } = useAppData();
  const { employeeOptions } = useCaisseEmployeeOptions({ valueField: 'id' });
  const responsibleSelectOptions = useMemo(
    () => buildCaisseEmployeeSelectOptions(employeeOptions, { emptyLabel: p('select_responsible') }),
    [employeeOptions, p],
  );
  const witnessSelectOptions = useMemo(
    () => buildCaisseEmployeeSelectOptions(employeeOptions, { emptyLabel: p('select_witness') }),
    [employeeOptions, p],
  );
  const { accounts: caisseAccounts } = useCaisseAccounts();
  const caisseSelectOptions = useMemo(() => {
    const empty = { value: '', label: p('select_caisse') };
    if (caisseAccounts.length) {
      return [empty, ...caisseAccounts.map((a) => ({ value: a.name, label: a.name }))];
    }
    return [
      empty,
      ...SAUDI_DEFAULT_REGISTERS.map((r) => ({ value: r.value, label: p(r.labelKey) })),
    ];
  }, [caisseAccounts, p]);

  const [searchTerm, setSearchTerm] = useState('');
  const { filters: columnFilters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const columnFilterRules = useMemo(() => [
    textColumnRule('operationNumber', (op) => op.reference || op.id || ''),
    textColumnRule('date', (op) => new Date(op.date).toLocaleDateString(dateLocale)),
    textColumnRule('caisse', (op) => op.caisse || ''),
    textColumnRule('affectation', (op) => op.affectation || ''),
    textColumnRule('designation', (op) => op.designation || ''),
    textColumnRule('credit', (op) => (op.credit > 0 ? formatBrandMoney(op.credit, false) : '')),
    textColumnRule('debit', (op) => (op.debit > 0 ? formatBrandMoney(op.debit, false) : '')),
    textColumnRule('balance', (op) => formatBrandMoney(op.solde, false)),
    exactColumnRule('status', (op) => op.status || ''),
  ], [dateLocale]);
  const [filterType, setFilterType] = useState('all'); // all, credit, debit
  const [showAddForm, setShowAddForm] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferType, setTransferType] = useState('mensuelle'); // uniquement vers mensuelle
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [operations, setOperations] = useState([]);
  const [defaultCaisseId, setDefaultCaisseId] = useState(1);
  const [loading, setLoading] = useState(false);
  const [editingOperationId, setEditingOperationId] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);

  const loadOperations = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, dashboard] = await Promise.all([
        localApiAdapter.get('/treasury/main').catch(() => []),
        localApiAdapter.get('/tresorerie/position/by-type/caisse').catch(() => null),
      ]);
      const principal = (dashboard?.accounts || dashboard?.balanceByCaisse || []).find((c) => c.type === 'principal');
      if (principal?.id) setDefaultCaisseId(principal.id);
      setOperations(sortRecordsNewestFirst((Array.isArray(rows) ? rows : []).map(mapPrincipalOperationFromApi)));
    } catch (error) {
      console.error('Failed to load principal operations:', error);
      toast.error(p('toast_transfer_error'));
      setOperations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOperations();
  }, [loadOperations]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [principaleFormTab, setPrincipaleFormTab] = useState('general');
  const principaleFormTabDefs = useMemo(() => ([
    { id: 'general', label: p('section_general'), icon: FileText, iconClassName: 'text-emerald-400' },
    { id: 'responsible', label: p('section_responsible'), icon: Shield, iconClassName: 'text-blue-400' },
  ]), [p]);
  const PRINCIPALE_FIELD_COUNT = 9;
  const principaleFormTabs = useMemo(
    () => buildCaisseModalFormTabs(principaleFormTabDefs, PRINCIPALE_FIELD_COUNT),
    [principaleFormTabDefs],
  );

  const transferFormTabDefs = useMemo(() => ([
    { id: 'transfer', label: p('section_general'), icon: FileText, iconClassName: 'text-emerald-400' },
    { id: 'responsible', label: p('section_responsible'), icon: Shield, iconClassName: 'text-blue-400' },
  ]), [p]);
  const PRINCIPALE_TRANSFER_FIELD_COUNT = 6;
  const transferFormTabs = useMemo(
    () => buildCaisseModalFormTabs(transferFormTabDefs, PRINCIPALE_TRANSFER_FIELD_COUNT),
    [transferFormTabDefs],
  );
  const [transferFormTab, setTransferFormTab] = useState('transfer');

  const getDefaultFormData = () => ({
    date: new Date().toISOString().split('T')[0],
    reference: '',
    designation: '',
    credit: '',
    debit: '',
    affectation: '',
    notes: '',
    caisse: '',
    responsableId: '',
    temoinId: '',
  });

  const getDefaultTransferData = () => ({
    date: new Date().toISOString().split('T')[0],
    montant: '',
    description: '',
    operation_id: '',
    responsableId: '',
    temoinId: '',
  });

  const openAddFormModal = () => {
    setPrincipaleFormTab('general');
    setEditingOperationId(null);
    setFormData({
      ...getDefaultFormData(),
      reference: nextCaisseEntityRef(
        settings,
        'principalOperation',
        'OPM',
        operations,
        (op) => op.reference || op.id,
      ),
    });
    setShowAddForm(true);
  };

  const closeAddFormModal = () => {
    setPrincipaleFormTab('general');
    setEditingOperationId(null);
    setFormData(getDefaultFormData());
    setShowAddForm(false);
  };

  const openTransferModal = () => {
    setTransferFormTab('transfer');
    setShowTransferModal(true);
  };

  const closeTransferModal = () => {
    setTransferFormTab('transfer');
    setTransferData(getDefaultTransferData());
    setSelectedOperation(null);
    setShowTransferModal(false);
  };
  
  // New UI Design States
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all'); // 'all', 'par_type', 'par_statut'
  const [quickFilter, setQuickFilter] = useState('all'); // 'all', 'credits', 'debits'
  
  // Date range filter
  
  // Dropdown states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  // Options de désignation prédéfinies
  const designationOptionKeys = ['designation_journal_feed', 'designation_journal_transfer', 'designation_monthly_transfer'];

  // Charger les codes d'affectation
  const [affectationCodes, setAffectationCodes] = useState([
    { id: '1', codeTypeId: 'CN', value: 'CN-01/RYD', description: 'الصندوق الرئيسي — الرياض', responsable: 'محمد العتيبي' },
    { id: '2', codeTypeId: 'ADM', value: 'ADM RYD', description: 'الإدارة — الرياض', responsable: 'مكتب الرياض' },
    { id: '3', codeTypeId: 'DIV', value: 'DIVERS', description: 'مصروفات متنوعة', responsable: 'المحاسبة' },
    { id: '4', codeTypeId: 'PR', value: 'PARC ROULANT', description: 'إدارة المركبات', responsable: 'قسم النقل' },
    { id: '5', codeTypeId: 'I', value: 'PRJ-2025/01', description: 'مشروع 2025/01', responsable: 'مدير المشروع' }
  ]);

  // Calcul des totaux
  const [totals, setTotals] = useState({
    credits: 0,
    debits: 0,
    solde: 0
  });

  // Calcul du solde pour chaque opération
  const [operationsWithSolde, setOperationsWithSolde] = useState([]);

  // Mettre à jour operationsWithSolde quand operations change
  useEffect(() => {
    let runningSolde = 0;
    const updatedOpsWithSolde = operations.map((op, index) => {
      if (index === 0) {
        runningSolde = op.credit - op.debit;
      } else {
        runningSolde += op.credit - op.debit;
      }
      return { ...op, solde: runningSolde };
    });
    
    setOperationsWithSolde(updatedOpsWithSolde);
    
    // Mettre à jour les totaux
    setTotals({
      credits: operations.reduce((acc, op) => acc + op.credit, 0),
      debits: operations.reduce((acc, op) => acc + op.debit, 0),
      solde: operations.reduce((acc, op) => acc + op.credit - op.debit, 0)
    });
  }, [operations]);

  // Données pour formulaire d'ajout d'opération
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    reference: '',
    designation: '',
    credit: '',
    debit: '',
    affectation: '',
    notes: '',
    caisse: '',
    // Nouveaux champs pour les employés
    responsableId: '',
    temoinId: ''
  });

  // Données pour transfert
  const [transferData, setTransferData] = useState({
    date: new Date().toISOString().split('T')[0],
    montant: '',
    description: '',
    operation_id: '',
    // Nouveaux champs pour les employés
    responsableId: '',
    temoinId: ''
  });

  // Liste des caisses journalières
  const [caisses, setCaisses] = useState([
    { id: '1', nom: 'يومية CN-01/RYD', responsable: 'محمد العتيبي' },
    { id: '2', nom: 'يومية PR-01', responsable: 'قسم النقل' },
    { id: '3', nom: 'البنك الأهلي السعودي', responsable: 'المحاسبة' },
    { id: '4', nom: 'بنك الراجحي', responsable: 'المحاسبة' }
  ]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'responsableId' && value) {
      setFormData({
        ...formData,
        [name]: value,
      });
      return;
    }
    
    if (name === 'credit' && value) {
      setFormData({
        ...formData,
        [name]: value,
        debit: ''
      });
      return;
    }
    
    if (name === 'debit' && value) {
      setFormData({
        ...formData,
        [name]: value,
        credit: ''
      });
      return;
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleTransferChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'responsableId' && value) {
      setTransferData({
        ...transferData,
        [name]: value,
      });
      return;
    }
    
    setTransferData({
      ...transferData,
      [name]: value
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.credit && !formData.debit) {
      toast.error(p('toast_amount_required'));
      return;
    }
    
    if (!formData.affectation) {
      toast.error(p('toast_affectation_required'));
      return;
    }
    
    try {
      const payload = mapPrincipalOperationToApi(
        editingOperationId
          ? formData
          : {
              ...formData,
              reference: formData.reference || nextCaisseEntityRef(
                settings,
                'principalOperation',
                'OPM',
                operations,
                (op) => op.reference || op.id,
              ),
            },
        defaultCaisseId,
      );
      if (editingOperationId) {
        await localApiAdapter.put(`/treasury/main/${editingOperationId}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/treasury/main', payload);
        notifyCreated(showNotify, entityLabel);
      }
      closeAddFormModal();
      await loadOperations();
    } catch (error) {
      console.error('Principal operation save error:', error);
      notifyError(showNotify, error.message || p('toast_transfer_error'));
    }
  };

  const handleViewOperation = (operation) => {
    setViewingRecord(operation);
    setShowViewModal(true);
  };

  const handleEditOperation = (operation) => {
    setEditingOperationId(operation.id);
    setFormData({
      ...operation,
      date: operation.date || new Date().toISOString().split('T')[0],
      credit: operation.credit > 0 ? String(operation.credit) : '',
      debit: operation.debit > 0 ? String(operation.debit) : '',
    });
    setPrincipaleFormTab('general');
    setShowAddForm(true);
  };

  const handleDeleteOperation = async (id) => {
    if (!window.confirm(p('confirm_delete'))) return;
    try {
      await localApiAdapter.delete(`/treasury/main/${id}`);
      notifyDeleted(showNotify, entityLabel);
      await loadOperations();
    } catch (error) {
      console.error('Delete operation error:', error);
      notifyError(showNotify, error.message || p('toast_transfer_error'));
    }
  };

  const clickableStatusOptions = useMemo(() => ([
    { value: 'pending', label: t('modules.caisse.pages.caisseReconciliationPage.status_pending'), icon: Clock, style: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
    { value: 'validated', label: t('modules.caisse.pages.caisseReconciliationPage.status_validated'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'cancelled', label: t('modules.caisse.pages.fundsTransferPage.status_cancelled'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
  ]), [t]);

  const principaleColumnKeys = useMemo(
    () => ['operationNumber', 'date', 'caisse', 'affectation', 'designation', 'credit', 'debit', 'balance', 'status', 'actions'],
    [],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useCaisseColumnVisibility(principaleColumnKeys);
  const qawaemColumns = useMemo(
    () => buildCaisseColumnDefs([
      { key: 'operationNumber', labelKey: 'th_operation_number' },
      { key: 'date', labelKey: 'th_date' },
      { key: 'caisse', labelKey: 'th_caisse' },
      { key: 'affectation', labelKey: 'th_affectation' },
      { key: 'designation', labelKey: 'th_designation' },
      { key: 'credit', labelKey: 'th_credit' },
      { key: 'debit', labelKey: 'th_debit' },
      { key: 'balance', labelKey: 'th_balance' },
      { key: 'status', label: t('common.status') },
      { key: 'actions', label: t('common.actions'), required: true },
    ], p, columnVisibility),
    [p, t, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = caisseTableColSpan(qawaemColumns);

  const handleStatusChange = async (recordId, newStatus) => {
    setOperations((prev) => prev.map((op) => (op.id === recordId ? { ...op, status: newStatus } : op)));
    try {
      await localApiAdapter.patch(`/treasury/main/${recordId}/status`, { status: newStatus });
    } catch (error) {
      try {
        await localApiAdapter.put(`/treasury/main/${recordId}`, { status: newStatus });
      } catch (innerError) {
        console.error('Principal operation status change error:', innerError);
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedRecords.length) return;
    if (!window.confirm(p('confirm_delete_selected', { count: selectedRecords.length }))) return;
    try {
      const deleteCount = selectedRecords.length;
      await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`/treasury/main/${id}`)));
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
      clearSelection();
      await loadOperations();
    } catch (error) {
      console.error('Bulk delete error:', error);
      notifyError(showNotify, error.message || p('toast_transfer_error'));
    }
  };

  const handleTransferSubmit = (e) => {
    e.preventDefault();
    
    if (!transferData.montant) {
      toast.error(p('toast_transfer_amount_required'));
      return;
    }
    
    // Récupérer les informations sur le responsable et le témoin
    const responsableMeta = transferData.responsableId
      ? findCaisseEmployeeMeta(employeeOptions, transferData.responsableId)
      : null;
    const temoinMeta = transferData.temoinId
      ? findCaisseEmployeeMeta(employeeOptions, transferData.temoinId)
      : null;
    
    // Transférer vers la Caisse Mensuelle via le contexte
    const result = transferPrincipaleToMensuelle({
      date: transferData.date,
      montant: Number(transferData.montant),
      description: transferData.description,
      operation_id: transferData.operation_id,
      affectation: selectedOperation ? selectedOperation.affectation : "DIV",
      // Ajouter les informations sur le responsable et le témoin
      responsableId: transferData.responsableId || null,
      responsable: responsableMeta?.name || '',
      temoinId: transferData.temoinId || null,
      temoin: temoinMeta?.name || ''
    });
    
    if (result) {
      toast.success(p('toast_transfer_success'));
      closeTransferModal();
    } else {
      toast.error(p('toast_transfer_error'));
    }
  };

  const caisseStats = useMemo(() => {
    if (caisseAccounts.length) {
      return caisseAccounts.map((a) => ({
        nom: a.name,
        solde: resolveCaisseAccountBalance(a),
      }));
    }
    const journal = operations.find((op) => op.caisse?.includes('Journal'));
    return [
      { nom: 'Journal CN°03/A.B', solde: journal?.solde || 0 },
      { nom: 'Journal PR-01', solde: 0 },
      { nom: 'Journal I/16/23', solde: 0 },
      { nom: 'Caisse Mensuelle', solde: totals.solde },
    ];
  }, [caisseAccounts, operations, totals.solde]);

  // Filtrer les opérations disponibles pour transfert (uniquement les crédits)
  const operationsForTransfer = operations.filter(op => op.credit > 0 && op.caisse !== "Caisse Mensuelle");

  // Filtrer les opérations selon le terme de recherche et le type
  const filteredOperations = useMemo(() => {
    const base = operationsWithSolde.filter((operation) => {
      const matchesSearch =
        (operation.designation && operation.designation.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (operation.reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        operation.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (operation.affectation && operation.affectation.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (operation.caisse && operation.caisse.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesType =
        filterType === 'all' ||
        (filterType === 'credit' && operation.credit > 0) ||
        (filterType === 'debit' && operation.debit > 0);

      const matchesQuick =
        quickFilter === 'all' ||
        (quickFilter === 'credits' && operation.credit > 0) ||
        (quickFilter === 'debits' && operation.debit > 0);

      return matchesSearch && matchesType && matchesQuick;
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [operationsWithSolde, searchTerm, filterType, quickFilter, columnFilters, columnFilterRules]);

  const pagedOperations = useMemo(
    () => sliceCaisseListItems(filteredOperations, treasuryConfig.itemsPerPage),
    [filteredOperations, treasuryConfig.itemsPerPage],
  );

  const {
    selectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
    clearSelection,
  } = useQawaemFilteredRowSelection(filteredOperations);

  const exportRows = mapOperationsForExport(filteredOperations);
  const exportHeaders = operationExportHeaders(p);
  const exportBaseName = `caisse_principale_${new Date().toISOString().slice(0, 10)}`;

  const handleExportCSV = () => {
    if (!exportRows.length) { toast.error(p('empty_message')); return; }
    exportTreasuryCSV(exportRows, exportHeaders, `${exportBaseName}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = async () => {
    if (!exportRows.length) { toast.error(p('empty_message')); return; }
    await exportTreasuryExcel(exportRows, exportHeaders, `${exportBaseName}.xlsx`, p('title'));
    setShowExportDropdown(false);
  };

  const handleExportPDF = async () => {
    if (!exportRows.length) { toast.error(p('empty_message')); return; }
    setIsGeneratingPDF(true);
    try {
      await exportTreasuryPDF(p('title'), exportHeaders, exportRows);
    } finally {
      setIsGeneratingPDF(false);
      setShowExportDropdown(false);
    }
  };

  const handleImportFile = (file) => {
    const headers = operationImportHeaders(p);
    const schema = {
      requiredFields: ['date', 'designation'],
      validators: {
        credit: (v) => !v || !Number.isNaN(Number(v)),
        debit: (v) => !v || !Number.isNaN(Number(v)),
      },
    };
    parseTreasuryImport(file, headers, schema)
      .then(async (result) => {
        if (!result.success) {
          toast.error(result.errors?.[0] || p('toast_transfer_error'));
          return;
        }
        let imported = 0;
        for (const row of result.data) {
          const credit = Number(row.credit) || 0;
          const debit = Number(row.debit) || 0;
          if (!credit && !debit) continue;
          const payload = mapPrincipalOperationToApi(
            {
              date: row.date,
              designation: row.designation,
              credit: credit ? String(credit) : '',
              debit: debit ? String(debit) : '',
              affectation: row.affectation || 'DEFAULT',
            },
            defaultCaisseId
          );
          await localApiAdapter.post('/treasury/main', payload);
          imported += 1;
        }
        toast.success(p('import_success', { count: imported }));
        await loadOperations();
      })
      .catch((err) => {
        console.error('Import error:', err);
        toast.error(p('toast_transfer_error'));
      })
      .finally(() => setShowImportDropdown(false));
  };
  
  // Main view tabs configuration
  const mainTabs = [
    { id: 'all', nameKey: 'tab_overview', icon: LayoutGrid },
    { id: 'par_type', nameKey: 'tab_by_type', icon: Shield },
    { id: 'par_statut', nameKey: 'tab_by_status', icon: Layers }
  ];
  
  // Quick filter buttons configuration
  const quickFilterButtons = [
    { id: 'all', nameKey: 'filter_all', icon: Wallet },
    { id: 'credits', nameKey: 'filter_credits', icon: DollarSign },
    { id: 'debits', nameKey: 'filter_debits', icon: CreditCard }
  ];

  const principaleStatCards = useMemo(() => ([
    { titleKey: 'stat_total_credits', value: formatBrandMoney(totals.credits), icon: DollarSign },
    { titleKey: 'stat_total_debits', value: formatBrandMoney(totals.debits), icon: CreditCard },
    { titleKey: 'stat_main_balance', value: formatBrandMoney(totals.solde), icon: Wallet },
    { titleKey: 'stat_operations_count', value: operations.length, icon: Layers },
  ]), [totals.credits, totals.debits, totals.solde, operations.length]);

  const qawaemStats = useMemo(() => sliceQawaemStats(
    principaleStatCards.map((s, i) => ({
      key: s.titleKey,
      label: p(s.titleKey),
      value: s.value,
      icon: s.icon,
      variant: i % 2 ? 'forest' : 'copper',
    })),
    4,
  ), [principaleStatCards, p]);

  return (
    <>
      <QawaemErpListPageShell
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Wallet}
        stats={qawaemStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={selectedRecords.length > 0 ? (
          <button
            type="button"
            onClick={handleDeleteSelected}
            className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
          >
            <Trash2 size={16} />
            <span>{t('common.delete')} ({selectedRecords.length})</span>
          </button>
        ) : null}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: 'بحث…' })}
        onCreate={openAddFormModal}
        createLabel={p('add_button')}
        importExport={{
          importOpen: showImportDropdown,
          exportOpen: showExportDropdown,
          onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
          onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
          importRef: importDropdownRef,
          exportRef: exportDropdownRef,
          importLabel: p('import'),
          exportLabel: p('export'),
          generatingLabel: t('common.loading'),
          isGeneratingPDF,
          onImportCSV: () => { triggerTreasuryImport(handleImportFile, '.csv'); setShowImportDropdown(false); },
          onImportExcel: () => { triggerTreasuryImport(handleImportFile); setShowImportDropdown(false); },
          onExportCSV: handleExportCSV,
          onExportExcel: handleExportExcel,
          onExportPDF: handleExportPDF,
        }}
        headerActions={(
          <>
            <button type="button" onClick={loadOperations} className="refresh-button" title={p('refresh')}>
              <RefreshCcw size={20} />
            </button>
            <button
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              className={`${
                showAdvancedOptions
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600 border-purple-400'
                  : 'bg-white/5 border-white/20'
              } border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
              title={p('advanced_options')}
            >
              <Filter className="h-5 w-5" />
              <span className="hidden sm:inline">{p('advanced_options')}</span>
              {showAdvancedOptions ? (
                <ChevronDown className="h-4 w-4 rotate-180 transition-transform" />
              ) : (
                <ChevronDown className="h-4 w-4 transition-transform" />
              )}
            </button>
            <button
              onClick={openTransferModal}
              className="bg-white/10 text-white px-4 py-3 rounded-xl hover:bg-white/20 transition-all duration-300 flex items-center"
            >
              <RefreshCcw className="h-5 w-5 mr-2" />
              <span className="hidden sm:inline">{p('transfer')}</span>
            </button>
          </>
        )}
      >
      {/* Options Avancées - Collapsible */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="space-y-4">
              {/* Main View Tabs */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
                <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
                  {mainTabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setMainViewMode(tab.id)}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                        mainViewMode === tab.id
                          ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <tab.icon className="h-5 w-5" />
                      <span className="hidden sm:inline">{p(tab.nameKey)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Filter Buttons */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
                <div className="flex space-x-1 overflow-x-auto table-scroll">
                  {quickFilterButtons.map((btn) => (
                    <button
                      key={btn.id}
                      onClick={() => {
                        setQuickFilter(btn.id);
                        if (btn.id === 'all') setFilterType('all');
                        else if (btn.id === 'credits') setFilterType('credit');
                        else if (btn.id === 'debits') setFilterType('debit');
                      }}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${
                        quickFilter === btn.id
                          ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <btn.icon className="h-5 w-5" />
                      <span className="hidden sm:inline">{p(btn.nameKey)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Distribution Overview */}
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                <h3 className="text-xl font-semibold text-white mb-4">{p('distribution_title')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-white">{p('dist_credits')}</h4>
                      <div className="text-sm rounded-full px-2 py-0.5 bg-gradient-to-r from-green-500 to-green-600 text-white force-white">
                        {operations.filter(op => op.credit > 0).length}
                      </div>
                    </div>
                    <div className="w-full bg-white/10 h-3 rounded-full">
                      <div className="h-3 rounded-full bg-gradient-to-r from-green-500 to-green-600" style={{ width: `${operations.length > 0 ? (operations.filter(op => op.credit > 0).length / operations.length * 100) : 0}%` }}></div>
                    </div>
                    <p className="mt-2 text-sm text-white/60">{p('percent_of_operations', { percent: operations.length > 0 ? ((operations.filter(op => op.credit > 0).length / operations.length * 100).toFixed(1)) : 0 })}</p>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-white">{p('dist_debits')}</h4>
                      <div className="text-sm rounded-full px-2 py-0.5 bg-gradient-to-r from-red-500 to-red-600 text-white force-white">
                        {operations.filter(op => op.debit > 0).length}
                      </div>
                    </div>
                    <div className="w-full bg-white/10 h-3 rounded-full">
                      <div className="h-3 rounded-full bg-gradient-to-r from-red-500 to-red-600" style={{ width: `${operations.length > 0 ? (operations.filter(op => op.debit > 0).length / operations.length * 100) : 0}%` }}></div>
                    </div>
                    <p className="mt-2 text-sm text-white/60">{p('percent_of_operations', { percent: operations.length > 0 ? ((operations.filter(op => op.debit > 0).length / operations.length * 100).toFixed(1)) : 0 })}</p>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium text-white">{p('dist_total')}</h4>
                      <div className="text-sm rounded-full px-2 py-0.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white force-white">
                        {operations.length}
                      </div>
                    </div>
                    <div className="w-full bg-white/10 h-3 rounded-full">
                      <div className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-blue-600" style={{ width: '100%' }}></div>
                    </div>
                    <p className="mt-2 text-sm text-white/60">{p('percent_all_operations')}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Operations Table with Integrated Header */}
      <div className="glass-panel glass-table-shell overflow-visible">

        

        

        
        {viewMode === 'table' ? (
              <div className="overflow-x-auto table-horizontal-scroll">
                <table className="w-full clients-table data-table">
                  <thead className="glass-table-head">
                    <tr>
                      <QawaemTableSelectAllHeader
                        allRowsSelected={allRowsSelected}
                        someRowsSelected={someRowsSelected}
                        onChange={handleSelectAll}
                        selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })}
                        deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء التحديد' })}
                      />
                      {isColumnVisible('operationNumber') ? (
                        <TableColumnHeaderFilter columnKey="operationNumber" label={p('th_operation_number')} value={columnFilters.operationNumber || ''} onChange={(v) => setFilter('operationNumber', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                      ) : null}
                      {isColumnVisible('date') ? (
                        <TableColumnHeaderFilter columnKey="date" label={p('th_date')} value={columnFilters.date || ''} onChange={(v) => setFilter('date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                      ) : null}
                      {isColumnVisible('caisse') ? (
                        <TableColumnHeaderFilter columnKey="caisse" label={p('th_caisse')} value={columnFilters.caisse || ''} onChange={(v) => setFilter('caisse', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                      ) : null}
                      {isColumnVisible('affectation') ? (
                        <TableColumnHeaderFilter columnKey="affectation" label={p('th_affectation')} value={columnFilters.affectation || ''} onChange={(v) => setFilter('affectation', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                      ) : null}
                      {isColumnVisible('designation') ? (
                        <TableColumnHeaderFilter columnKey="designation" label={p('th_designation')} value={columnFilters.designation || ''} onChange={(v) => setFilter('designation', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                      ) : null}
                      {isColumnVisible('credit') ? (
                        <TableColumnHeaderFilter columnKey="credit" label={p('th_credit')} value={columnFilters.credit || ''} onChange={(v) => setFilter('credit', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="right" thClassName="text-white font-semibold" />
                      ) : null}
                      {isColumnVisible('debit') ? (
                        <TableColumnHeaderFilter columnKey="debit" label={p('th_debit')} value={columnFilters.debit || ''} onChange={(v) => setFilter('debit', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="right" thClassName="text-white font-semibold" />
                      ) : null}
                      {isColumnVisible('balance') ? (
                        <TableColumnHeaderFilter columnKey="balance" label={p('th_balance')} value={columnFilters.balance || ''} onChange={(v) => setFilter('balance', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="right" thClassName="text-white font-semibold" />
                      ) : null}
                      {isColumnVisible('status') ? (
                        <TableColumnHeaderFilter columnKey="status" label={t('common.status')} kind="select" options={clickableStatusOptions.map((o) => ({ value: o.value, label: o.label }))} value={columnFilters.status || ''} onChange={(v) => setFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold" />
                      ) : null}
                      <QawaemTableHeaderActionsCell
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                        label={t('common.actions')}
                        columns={qawaemColumns}
                        onToggle={handleToggleColumn}
                        visibleCount={qawaemVisibleColumnCount}
                        totalCount={qawaemColumns.length}
                      />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {pagedOperations.map((operation) => (
                      <tr 
                        key={operation.id} 
                        className="hover:bg-white/5 transition-colors"
                      >
                        <QawaemTableSelectRowCell
                          checked={selectedRecords.includes(operation.id)}
                          onChange={() => handleSelectRecord(operation.id)}
                        />
                        {isColumnVisible('operationNumber') ? (
                          <td className="px-6 py-4 text-white font-mono text-sm">{operation.reference || operation.id}</td>
                        ) : null}
                        {isColumnVisible('date') ? (
                          <td className="px-6 py-4 text-white/80">{new Date(operation.date).toLocaleDateString(dateLocale)}</td>
                        ) : null}
                        {isColumnVisible('caisse') ? (
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
                              {operation.caisse || p('default_caisse')}
                            </span>
                          </td>
                        ) : null}
                        {isColumnVisible('affectation') ? (
                          <td className="px-6 py-4 text-white">{operation.affectation || '-'}</td>
                        ) : null}
                        {isColumnVisible('designation') ? (
                          <td className="px-6 py-4 text-white font-medium">{operation.designation}</td>
                        ) : null}
                        {isColumnVisible('credit') ? (
                          <td className="px-6 py-4 text-right">
                            {operation.credit > 0 ? (
                              <span className="text-green-400 font-medium">
                                {formatBrandMoney(operation.credit, false)}
                              </span>
                            ) : '-'}
                          </td>
                        ) : null}
                        {isColumnVisible('debit') ? (
                          <td className="px-6 py-4 text-right">
                            {operation.debit > 0 ? (
                              <span className="text-red-400 font-medium">
                                {formatBrandMoney(operation.debit, false)}
                              </span>
                            ) : '-'}
                          </td>
                        ) : null}
                        {isColumnVisible('balance') ? (
                          <td className="px-6 py-4 text-right font-medium">
                            <span className={operation.solde >= 0 ? 'text-green-400' : 'text-red-400'}>
                              {formatBrandMoney(operation.solde, false)}
                            </span>
                          </td>
                        ) : null}
                        {isColumnVisible('status') ? (
                          <td className="px-6 py-4 text-center">
                            <div className="flex justify-center">
                              <QawaemClickableStatusBadge
                                status={operation.status || 'pending'}
                                recordId={operation.id}
                                options={clickableStatusOptions}
                                onChange={handleStatusChange}
                                title={t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                              />
                            </div>
                          </td>
                        ) : null}
                        <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                            onView={() => handleViewOperation(operation)}
                            onEdit={() => handleEditOperation(operation)}
                            onDelete={() => handleDeleteOperation(operation.id)}
                            viewTitle={p('view', { defaultValue: 'عرض' })}
                            editTitle={p('edit')}
                            deleteTitle={p('delete')}
                          />
                        </QawaemTableStickyActionsTd>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                {pagedOperations.map((operation, index) => (
                  <motion.div
                    key={operation.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4 mb-4">
                          <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            <Wallet size={20} className="text-emerald-400" />
                            {operation.reference || operation.id}
                          </h3>
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
                            {operation.caisse || p('default_caisse')}
                          </span>
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                            {new Date(operation.date).toLocaleDateString(dateLocale)}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                          {/* Informations Générales */}
                          <div>
                            <h5 className="font-medium text-white mb-2">{p('card_general_info')}</h5>
                            <div className="space-y-1 text-sm text-white/70">
                              <div><span className="font-medium">{p('op_number')}</span> {operation.reference || operation.id}</div>
                              <div><span className="font-medium">{p('date_label')}</span> {new Date(operation.date).toLocaleDateString(dateLocale)}</div>
                              <div><span className="font-medium">{p('caisse_label')}</span> {operation.caisse || p('default_caisse')}</div>
                              {operation.affectation && (
                                <div className="pt-2 border-t border-white/20">
                                  <span className="font-medium">{p('affectation_label')}</span>
                                  <div className="text-white/90 text-xs mt-1">{operation.affectation}</div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Désignation */}
                          <div>
                            <h5 className="font-medium text-white mb-2">{p('card_designation')}</h5>
                            <div className="space-y-1 text-sm text-white/70">
                              <div className="flex items-center gap-2">
                                <FileText size={16} className="text-blue-400" />
                                <span className="text-white">{operation.designation}</span>
                              </div>
                              {operation.notes && (
                                <div className="mt-2 p-2 bg-white/5 rounded">
                                  <span className="font-medium text-white/80 text-xs">{p('notes_label')}</span>
                                  <p className="text-white/60 text-xs mt-1">{operation.notes}</p>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Montants */}
                          <div>
                            <h5 className="font-medium text-white mb-2">{p('card_amounts')}</h5>
                            <div className="space-y-1 text-sm text-white/70">
                              {operation.credit > 0 && (
                                <div className="p-2 bg-green-500/10 rounded border border-green-500/30">
                                  <span className="font-medium text-green-300 text-xs">{p('credit_label')}</span>
                                  <p className="text-green-200 text-sm mt-1 font-semibold">{formatBrandMoney(operation.credit)}</p>
                                </div>
                              )}
                              {operation.debit > 0 && (
                                <div className="p-2 bg-red-500/10 rounded border border-red-500/30">
                                  <span className="font-medium text-red-300 text-xs">{p('debit_label')}</span>
                                  <p className="text-red-200 text-sm mt-1 font-semibold">{formatBrandMoney(operation.debit)}</p>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Solde */}
                          <div>
                            <h5 className="font-medium text-white mb-2">{p('card_balance')}</h5>
                            <div className="space-y-1 text-sm text-white/70">
                              <div className="p-2 bg-white/5 rounded border border-white/10">
                                <span className="font-medium text-white/80 text-xs">{p('balance_after_op')}</span>
                                <p className={`text-lg mt-1 font-bold ${operation.solde >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {formatBrandMoney(operation.solde)}
                                </p>
                              </div>
                              {operation.responsable && (
                                <div className="pt-2 border-t border-white/20">
                                  <span className="font-medium">{p('responsible_label')}</span>
                                  <div className="text-white/90 text-xs mt-1">{operation.responsable}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2 ml-4">
                        <QawaemClickableStatusBadge
                          status={operation.status || 'pending'}
                          recordId={operation.id}
                          options={clickableStatusOptions}
                          onChange={handleStatusChange}
                        />
                        <QawaemErpRowActions
                          compact
                          size={14}
                          onView={() => handleViewOperation(operation)}
                          onEdit={() => handleEditOperation(operation)}
                          onDelete={() => handleDeleteOperation(operation.id)}
                          viewTitle={p('view', { defaultValue: 'عرض' })}
                          editTitle={p('edit')}
                          deleteTitle={p('delete')}
                        />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )
        }
      </div>
      </QawaemErpListPageShell>

      {/* Add Form Modal - Style Journal de Caisse (100% identique) */}
      {showAddForm && ReactDOM.createPortal(
        <div className={QAWAEM_MODAL_OVERLAY_CLASS} role="presentation">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={getQawaemModalPanelClass(isLight)}
            style={getQawaemModalPanelStyle(isLight, '1200px')}
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
              <h2 className="text-2xl font-bold text-white">{editingOperationId ? p('edit') : p('form_new_operation')}</h2>
              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  form="principale-nouvelle-operation-form"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckSquare size={18} />
                  {p('save')}
                </button>
                <button type="button" onClick={closeAddFormModal} className="text-white/70 hover:text-white transition-colors" aria-label={p('close')}>
                  <X size={24} />
                </button>
              </div>
            </div>
            {principaleFormTabs.length > 0 ? (
              <QawaemErpModalFormTabBar tabs={principaleFormTabs} activeTab={principaleFormTab} onTabChange={setPrincipaleFormTab} isLight={isLight} ariaLabel={p('form_new_operation')} />
            ) : null}
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="principale-nouvelle-operation-form" onSubmit={handleFormSubmit}>
                {(principaleFormTabs.length === 0 || principaleFormTab === 'general') && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('th_operation_number', { defaultValue: 'الرمز' })}</label>
                      <input
                        type="text"
                        name="reference"
                        value={formData.reference || ''}
                        onChange={handleFormChange}
                        placeholder={p('th_operation_number')}
                        className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-mono"
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('date')} <span className="qawaem-required-mark">*</span></label>
                      <CustomDatePicker
                        value={formData.date}
                        onChange={(value) => setFormData({ ...formData, date: value })}
                        placeholder={p('select')}
                      />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('source_caisse')}</label>
                      <div style={{ minHeight: '42px' }}>
                        <CustomSelect
                          value={formData.caisse}
                          onChange={(value) => setFormData({ ...formData, caisse: value })}
                          options={caisseSelectOptions}
                          placeholder={p('select_caisse')}
                          searchable={true}
                        />
                      </div>
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('designation')} <span className="qawaem-required-mark">*</span></label>
                      <input
                        type="text"
                        name="designation"
                        value={formData.designation}
                        onChange={handleFormChange}
                        placeholder={p('designation_placeholder')}
                        className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        required
                      />
                    </div>
                    <div className="min-w-0 md:col-span-3">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('affectation_code')} <span className="qawaem-required-mark">*</span></label>
                      <div style={{ minHeight: '42px' }}>
                        <CustomSelect
                          value={formData.affectation}
                          onChange={(value) => setFormData({ ...formData, affectation: value })}
                          options={[
                            { value: '', label: p('select_affectation') },
                            ...affectationCodes.map((code) => ({
                              value: code.value,
                              label: `${code.value} - ${code.description}`
                            }))
                          ]}
                          placeholder={p('select_affectation')}
                          searchable={true}
                        />
                      </div>
                    </div>
                    {principaleFormTabs.length <= 2 ? (
                      <>
                        <div className="min-w-0">
                          <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('credit_amount')}</label>
                          <input type="number" name="credit" value={formData.credit} onChange={handleFormChange} placeholder={p('credit_placeholder')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
                        </div>
                        <div className="min-w-0">
                          <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('debit_amount')}</label>
                          <input type="number" name="debit" value={formData.debit} onChange={handleFormChange} placeholder={p('debit_placeholder')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
                        </div>
                      </>
                    ) : null}
                  </div>
                )}
                {principaleFormTabs.length > 2 && principaleFormTab === 'amounts' && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('credit_amount')}</label>
                      <input type="number" name="credit" value={formData.credit} onChange={handleFormChange} placeholder={p('credit_placeholder')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('debit_amount')}</label>
                      <input type="number" name="debit" value={formData.debit} onChange={handleFormChange} placeholder={p('debit_placeholder')} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" />
                    </div>
                  </div>
                )}
                {(principaleFormTabs.length === 0 || principaleFormTab === 'responsible') && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('responsible')}</label>
                      <div style={{ minHeight: '42px' }}>
                        <CustomSelect
                          value={formData.responsableId}
                          onChange={(value) => setFormData({ ...formData, responsableId: value })}
                          options={responsibleSelectOptions}
                          placeholder={p('select_responsible')}
                          searchable={true}
                        />
                      </div>
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('witness')}</label>
                      <div style={{ minHeight: '42px' }}>
                        <CustomSelect
                          value={formData.temoinId}
                          onChange={(value) => setFormData({ ...formData, temoinId: value })}
                          options={witnessSelectOptions}
                          placeholder={p('select_witness')}
                          searchable={true}
                        />
                      </div>
                    </div>
                    <div className="min-w-0 md:col-span-3">
                      <label className="block w-full text-white/80 mb-2 text-sm font-medium text-right">{p('notes')}</label>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleFormChange}
                        placeholder={p('notes_placeholder')}
                        className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                        rows="3"
                      ></textarea>
                    </div>
                    <div className="min-w-0 md:col-span-3">
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                        <p className="text-blue-300 text-sm">{p('documents_hint')}</p>
                      </div>
                    </div>
                  </div>
                )}
                {principaleFormTabs.length > 2 && principaleFormTab === 'documents' && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="min-w-0 md:col-span-3">
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                            <p className="text-blue-300 text-sm">
                              {p('documents_hint')}
                            </p>
                          </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Transfer Modal */}
      {showTransferModal && ReactDOM.createPortal(
        <div className={QAWAEM_MODAL_OVERLAY_CLASS} onClick={closeTransferModal} role="presentation">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className={getQawaemModalPanelClass(isLight)}
            style={getQawaemModalPanelStyle(isLight, '1200px')}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex justify-between items-center p-6 border-b shrink-0 ${isLight ? 'border-slate-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${cfgTheme.title}`}>
                {p('transfer_modal_title')}
              </h2>
              <button
                type="button"
                onClick={closeTransferModal}
                className={isLight ? 'text-slate-500 hover:text-slate-800' : 'text-white/60 hover:text-white'}
              >
                &times;
              </button>
            </div>
            {transferFormTabs.length > 0 ? (
              <QawaemErpModalFormTabBar tabs={transferFormTabs} activeTab={transferFormTab} onTabChange={setTransferFormTab} isLight={isLight} ariaLabel={p('transfer_modal_title')} />
            ) : null}
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
            <form id="principale-transfer-form" onSubmit={handleTransferSubmit}>
              {(transferFormTabs.length === 0 || transferFormTab === 'transfer') && (
                <div className={CAISSE_FORM_GRID}>
                  <div className="min-w-0">
                    <label className="block w-full text-white/80 text-sm font-medium mb-2 text-right">{p('transfer_date')}</label>
                    <input
                      type="date"
                      name="date"
                      value={transferData.date}
                      onChange={handleTransferChange}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/80 text-sm font-medium mb-2 text-right">{p('transfer_amount')}</label>
                    <input
                      type="number"
                      name="montant"
                      value={transferData.montant}
                      onChange={handleTransferChange}
                      placeholder={p('transfer_amount_placeholder')}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div className="min-w-0 md:col-span-3">
                    <label className="block w-full text-white/80 text-sm font-medium mb-2 text-right">{p('transfer_source_op')}</label>
                    <select
                      name="operation_id"
                      value={transferData.operation_id}
                      onChange={handleTransferChange}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">{p('select_operation')}</option>
                      {operationsForTransfer.map((op) => (
                        <option key={op.id} value={op.id}>
                          {op.id} - {op.designation} ({formatBrandMoney(op.credit)})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-white/50 mt-1">{p('transfer_source_hint')}</p>
                  </div>
                  {transferFormTabs.length === 0 ? (
                    <>
                      <div className="min-w-0">
                        <label className="block w-full text-white/80 text-sm font-medium mb-2 text-right">{p('responsible')}</label>
                        <CustomSelect
                          value={transferData.responsableId}
                          onChange={(value) => setTransferData({ ...transferData, responsableId: value })}
                          options={responsibleSelectOptions}
                          placeholder={p('select_responsible')}
                          searchable={true}
                        />
                      </div>
                      <div className="min-w-0">
                        <label className="block w-full text-white/80 text-sm font-medium mb-2 text-right">{p('witness')}</label>
                        <CustomSelect
                          value={transferData.temoinId}
                          onChange={(value) => setTransferData({ ...transferData, temoinId: value })}
                          options={witnessSelectOptions}
                          placeholder={p('select_witness')}
                          searchable={true}
                        />
                      </div>
                      <div className="min-w-0 md:col-span-3">
                        <label className="block w-full text-white/80 text-sm font-medium mb-2 text-right">{p('transfer_description')}</label>
                        <textarea name="description" value={transferData.description} onChange={handleTransferChange} placeholder={p('transfer_description_placeholder')} className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" rows="3" />
                      </div>
                    </>
                  ) : null}
                </div>
              )}
              {transferFormTabs.length > 0 && transferFormTab === 'responsible' && (
                <div className={CAISSE_FORM_GRID}>
                  <div className="min-w-0">
                    <label className="block w-full text-white/80 text-sm font-medium mb-2 text-right">{p('responsible')}</label>
                    <CustomSelect
                      value={transferData.responsableId}
                      onChange={(value) => setTransferData({ ...transferData, responsableId: value })}
                      options={responsibleSelectOptions}
                      placeholder={p('select_responsible')}
                      searchable={true}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block w-full text-white/80 text-sm font-medium mb-2 text-right">{p('witness')}</label>
                    <CustomSelect
                      value={transferData.temoinId}
                      onChange={(value) => setTransferData({ ...transferData, temoinId: value })}
                      options={witnessSelectOptions}
                      placeholder={p('select_witness')}
                      searchable={true}
                    />
                  </div>
                  <div className="min-w-0 md:col-span-3">
                    <label className="block w-full text-white/80 text-sm font-medium mb-2 text-right">{p('transfer_description')}</label>
                    <textarea
                      name="description"
                      value={transferData.description}
                      onChange={handleTransferChange}
                      placeholder={p('transfer_description_placeholder')}
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      rows="3"
                    ></textarea>
                  </div>
                </div>
              )}
              <div className="flex justify-end space-x-3 pt-4 mt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={closeTransferModal}
                  className="px-4 py-2 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
                >
                  {p('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl hover:from-blue-600 hover:to-blue-800 transition-all"
                >
                  {p('transfer_submit')}
                </button>
              </div>
            </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {showViewModal && viewingRecord ? (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => { setShowViewModal(false); setViewingRecord(null); }}
          title={p('view_operation', { defaultValue: 'عرض العملية' })}
          icon={CheckSquare}
          maxWidth="700px"
        >
              <div className={CAISSE_FORM_GRID}>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('th_operation_number')}</p>
                  <p className={`font-medium font-mono ${cfgTheme.title}`}>{viewingRecord.reference || viewingRecord.id}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('th_date')}</p>
                  <p className={`font-medium ${cfgTheme.title}`}>{new Date(viewingRecord.date).toLocaleDateString(dateLocale)}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('th_caisse')}</p>
                  <p className={`font-medium ${cfgTheme.title}`}>{viewingRecord.caisse || p('default_caisse')}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('th_affectation')}</p>
                  <p className={`font-medium ${cfgTheme.title}`}>{viewingRecord.affectation || '-'}</p>
                </div>
                <div className="md:col-span-3">
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('th_designation')}</p>
                  <p className={`font-medium ${cfgTheme.title}`}>{viewingRecord.designation}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('th_credit')}</p>
                  <p className="text-green-500 font-bold">{viewingRecord.credit > 0 ? formatBrandMoney(viewingRecord.credit) : '-'}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('th_debit')}</p>
                  <p className="text-red-500 font-bold">{viewingRecord.debit > 0 ? formatBrandMoney(viewingRecord.debit) : '-'}</p>
                </div>
                <div>
                  <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('th_balance')}</p>
                  <p className={`font-bold ${viewingRecord.solde >= 0 ? 'text-green-500' : 'text-red-500'}`}>{formatBrandMoney(viewingRecord.solde ?? 0)}</p>
                </div>
                {viewingRecord.notes ? (
                  <div className="md:col-span-3">
                    <p className={`text-sm mb-1 ${cfgTheme.subtitle}`}>{p('notes')}</p>
                    <p className={`font-medium ${cfgTheme.title}`}>{viewingRecord.notes}</p>
                  </div>
                ) : null}
              </div>
        </QawaemErpViewModal>
      ) : null}

      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default CaissePrincipalePage; 


